import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { css } from '@emotion/react';
import { Button } from '@mui/material';
import { CSSProperties, JSX, PropsWithChildren, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { IForbiddenErrorConfig } from '../../../components/Fallback/GenericErrorFallback';
import { useShowSummaryV2 } from './useShowSummaryV2';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { SubSection } from '../../CompanyProfiles/Summary/components/SubSection';
import { Panel, PanelPagination } from './Panel';

const paginationRowCss = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '0.5rem',
});

export interface ISummarySectionProps extends PropsWithChildren {
  /** V2 only. Header control next to the title; `Panel` has no equivalent slot. */
  actionComponent?: JSX.Element;
  /** V2 only. Right-aligned header controls — the chart's expand button and the like. */
  actions?: ReactNode;
  /** V2 only. Styles for the bordered body. */
  childrenContainerStyle?: CSSProperties;
  /** Initial page for `paginationConfig`. */
  defaultPage?: number;
  forbiddenFallback?: IForbiddenErrorConfig;
  /**
   * Paginator for the card's content. V2 renders it inside the collapsible body, so a collapsed
   * card offers no page controls for content it is not showing; V1 puts it in `Panel`'s header.
   */
  paginationConfig?: { pageCount: number; onChange: (page: number) => void };
  /**
   * V2: body scrolls on Y unless a card's inline inputs need to overflow on X (Y and X overflow
   * cannot both be relaxed on one element). V1 maps the inverse onto `showContentOverflow`.
   */
  scrollBody?: boolean;
  /** Navigation out of the card. V1 renders `Panel`'s chevron; V2 a labelled button. */
  showMore?: { url: string; title: string };
  title: string;
}

/**
 * A card on the Summary tab, and the single place the `showSummaryV2` flag is resolved for the
 * cards whose only difference between the two designs is their container.
 *
 * Flag off — `Panel`, exactly as `main` renders it.
 * Flag on — `SubSection`, with the V2 arrangement.
 *
 * Cards whose *content* differs between the designs (the Digest's single list, Company Details'
 * "Show All", Investment Metrics' header, the chart's `ChartPanel`) branch on the flag themselves;
 * this component cannot express those.
 */
export function SummarySection(props: ISummarySectionProps) {
  const showSummaryV2 = useShowSummaryV2();

  return showSummaryV2 ? <SummarySectionV2 {...props} /> : <SummarySectionV1 {...props} />;
}

/** The pre-`showSummaryV2` card: `Panel`, with the props each caller passed on `main`. */
function SummarySectionV1({
  children,
  defaultPage,
  forbiddenFallback,
  paginationConfig,
  scrollBody = true,
  showMore,
  title,
}: ISummarySectionProps) {
  const panel = (
    <Panel
      defaultPage={defaultPage}
      paginationConfig={paginationConfig}
      showContentOverflow={!scrollBody}
      showMore={showMore}
      title={title}
    >
      {children}
    </Panel>
  );

  // `Panel` wraps itself in a `GenericFallbacksWrapper` already, but a card needing a
  // `forbiddenFallback` had a second one outside it on `main` — that is where the config lived.
  if (!forbiddenFallback) {
    return panel;
  }
  return <GenericFallbacksWrapper forbiddenFallback={forbiddenFallback}>{panel}</GenericFallbacksWrapper>;
}

/**
 * The `showSummaryV2` card. Wraps `SubSection` with the props every V2 card shares, so the contract
 * lives in one place rather than being restated at each call site:
 *
 * - `noCard` — suppresses `SubSection`'s legacy card styling (hardcoded #fff and gradients). The
 *   card chrome comes from the grid slot's `summaryCardCss` instead, so it encloses the title as
 *   well as the content, the way the design draws it.
 * - `margin: 0` — `SubSection` defaults to `margin: 2rem 0`, which the grid does not want
 * - `withFallbacks` — the error boundary `Panel` gave every card
 * - collapsible, expanded by default, with the collapsed body unmounted so content that measures
 *   itself on mount (the echarts cards) sizes against a real box when it is re-expanded
 * - a no-data fallback when the body renders nothing, in place of an empty bordered box
 */
function SummarySectionV2({
  actionComponent,
  actions,
  childrenContainerStyle,
  children,
  defaultPage,
  forbiddenFallback,
  paginationConfig,
  scrollBody = true,
  showMore,
  title,
}: ISummarySectionProps) {
  // A card given no body would otherwise expand to an empty bordered box. Paginating content that
  // isn't there makes no sense either, so the paginator goes with it.
  //
  // This only catches a card that passes nothing — it cannot see a child that renders null, so
  // cards whose data may be missing still supply their own empty state (as Leadership, Investment
  // Metrics, Last Activity and Strongest Connections already do).
  const hasContent = children !== null && children !== undefined && children !== false;

  return (
    <SubSection
      actionComponent={actionComponent}
      actions={
        showMore ? (
          <>
            {actions}
            <SectionLinkButton label={showMore.title} url={showMore.url} />
          </>
        ) : (
          actions
        )
      }
      collapseProps={{ collapsible: true, defaultExpanded: true, unmountOnExit: true }}
      forbiddenFallback={forbiddenFallback}
      noCard
      style={{ margin: 0 }}
      title={title}
      withFallbacks
      childrenContainerStyle={{
        // The chrome lives on the grid slot (`summaryCardCss`) so it encloses the title too, which
        // leaves the body to supply only the design's 12px gap below the header. `padding: 0`
        // overrides the 1rem CardWrapper applies in `noCard` mode.
        marginTop: '0.75rem',
        padding: 0,
        ...(scrollBody ? { overflowY: 'auto' } : {}),
        ...childrenContainerStyle,
        // Last, so it beats a caller's floor (the chart's 275px, Digest's 26rem) — those exist to
        // give real content room, and would otherwise stretch the empty-state box.
        ...(hasContent ? {} : { minHeight: 0 }),
      }}
    >
      {hasContent ? (
        <>
          {paginationConfig && (
            <div css={paginationRowCss}>
              <PanelPagination {...paginationConfig} defaultPage={defaultPage} />
            </div>
          )}
          {children}
        </>
      ) : (
        <NoDataMessage2 />
      )}
    </SubSection>
  );
}

/**
 * "Go to X" navigation for a V2 card header. A labelled outlined button rather than `Panel`'s bare
 * chevron, matching the Portfolio page's "Select KPIs" control (`PopupMenu.tsx:178`).
 */
export function SectionLinkButton({ label, url }: { label: string; url: string }) {
  const navigate = useNavigate();

  return (
    <Button
      color={'secondary'}
      endIcon={<ChevronRightIcon />}
      onClick={() => navigate(url)}
      variant={'outlined'}
    >
      {label}
    </Button>
  );
}
